import type { TFunction } from 'i18next';
import type {
  ActionPlan,
  PlanArea,
  PlanMeasure,
  WorkEnvironmentSurvey,
} from '../../api/workEnvironmentTypes';
import { WorkEnvironmentSurveyStatus } from '../../api/workEnvironmentTypes';
import { areasByRisk, isDealtWith } from './planView';
import { dayAndMonth } from './surveyProcessView';

/** Step 8: the approved plans are carried out and followed up */
export const showsFollowUpPage = (
  survey: Pick<WorkEnvironmentSurvey, 'status' | 'riskCalculatedAt'>,
) =>
  Boolean(survey.riskCalculatedAt) &&
  survey.status === WorkEnvironmentSurveyStatus.ACTION_PLAN_COMPLETED;

/** A plan changed after approval is still followed up while it waits for a new approval */
const isFollowedUp = (plan: ActionPlan | null) =>
  Boolean(plan && (plan.approved || plan.reopenedAt));

export const followedUpAreas = (areas: PlanArea[]) =>
  areas.filter((area) => isFollowedUp(area.plan));

const countOf = (measures: PlanMeasure[], status: PlanMeasure['status']) =>
  measures.filter((measure) => measure.status === status).length;

export const measureProgress = (measures: PlanMeasure[]) => ({
  done: countOf(measures, 'DONE'),
  inProgress: countOf(measures, 'IN_PROGRESS'),
  notStarted: countOf(measures, 'NOT_STARTED'),
  total: measures.length,
  nextDue:
    measures
      .filter((measure) => measure.status !== 'DONE')
      .map((measure) => measure.dueDate)
      .sort()[0] ?? null,
});

/** «1 pågår og 1 er ikke startet. Neste frist er 15. november.» */
export const statusLine = (
  progress: ReturnType<typeof measureProgress>,
  t: TFunction,
  language: string,
) => {
  const counts = [
    progress.inProgress > 0 &&
      t('workEnvironmentFollowUp.status.inProgress', {
        count: progress.inProgress,
      }),
    progress.notStarted > 0 &&
      t('workEnvironmentFollowUp.status.notStarted', {
        count: progress.notStarted,
      }),
  ].filter(Boolean);
  return `${counts.join(` ${t('workEnvironmentFollowUp.status.and')} `)}. ${t(
    'workEnvironmentFollowUp.status.nextDue',
    {
      date: dayAndMonth(progress.nextDue!, language),
    },
  )}`;
};

/** Areas at risk that still need a plan or a decision before HR's deadline */
export const otherAreasAtRisk = (areas: PlanArea[]) => {
  const open = areasByRisk(areas).atRisk.filter(
    (area) => !isDealtWith(area) && !isFollowedUp(area.plan),
  );
  return {
    count: open.length,
    deadline:
      open
        .flatMap((area) => area.deadline ?? [])
        .sort()
        .at(0) ?? null,
  };
};

export type ReviewError = 'date' | 'already' | 'failed';

/** The API checks the day of a review. The page tells which field to fix, or that it went wrong. */
export const reviewError = (error: unknown): ReviewError | null => {
  if (!error) return null;
  const response = (
    error as { response?: { status?: number; data?: { code?: string } } }
  ).response;
  if (response?.status === 400) return 'date';
  if (response?.status === 409 && response.data?.code === 'alreadyReviewed')
    return 'already';
  return 'failed';
};
